import React from "react";

import CheckIcon from "@uui/Check";

import ProviderButton from "@app/analytics/components/ProviderButton";
import useIntegrations from "@app/analytics/hooks/integrations/useIntegrations";
import type {
  IntegrationProvider,
  IntegrationProviderId,
} from "@app/common/types/providerTypes";
import Config from "@app/config";

import IconBackground from "@rui/IconBackground";
import { Img } from "@rui/styles";
import TooltipWrapper from "@rui/TooltipWrapper";

const ConnectableIntegrationProvider = ({
  hideIfNotConnected,
  provider,
  onConnect,
  isConnecting = false,
}: {
  hideIfNotConnected?: boolean;
  provider: IntegrationProvider;
  onConnect: (providerId: IntegrationProviderId) => void;
  isConnecting?: boolean;
}) => {
  const { getIntegration } = useIntegrations();

  const integration = getIntegration({ providerId: provider.id });
  const isConnected = Boolean(integration?.isConnected);
  const isIntegrationDisabled = Boolean(integration?.isDisabled);
  const canConnectIntegration = !isConnected && !isIntegrationDisabled;

  if (!isConnected && hideIfNotConnected) return null;

  const handleConnect = () => {
    onConnect(provider.id);
  };

  return (
    <TooltipWrapper
      show={isConnected || isIntegrationDisabled}
      title={
        isConnected
          ? `${provider.displayNameShort} is already connected`
          : "Your Workspace has restricted access to this integration. It is not available to use with Read at this time."
      }
      enterDelay={isConnected ? 2000 : 0}
      wrapperSx={{ width: "100%" }}
    >
      <ProviderButton
        icon={
          <Img
            src={provider.icon}
            alt={`Continue with ${provider.displayNameShort}`}
          />
        }
        providerId={provider.id}
        onClick={canConnectIntegration ? handleConnect : undefined}
        disabled={!canConnectIntegration}
        className={isIntegrationDisabled ? "disabled" : undefined}
        loading={isConnecting}
        endIcon={
          isConnected ? (
            <IconBackground height={24} bgcolor="shades.meadow.50">
              <CheckIcon sx={{ color: "shades.meadow.500" }} />
            </IconBackground>
          ) : undefined
        }
        sx={{
          width: "100%",
          boxShadow: isConnected ? undefined : "0px 1px 16px 0px #231F380A",
        }}
      >
        {Config.flags.googleReview && "Connect with "}
        {provider.displayNameShort}
      </ProviderButton>
    </TooltipWrapper>
  );
};

export default ConnectableIntegrationProvider;
